/**
 * Component Name: Metadata
 *
 * Description:
 * The Metadata component dynamically generates meta tags for SEO and social media sharing.
 * It automatically appends " | General Genomics" to the page title and ensures consistency
 * in metadata such as description, URL, and social media images. By default, it also handles
 * Open Graph (OG) and Twitter Card meta tags.
 *
 * Usage:
 * Include this component at the top of any page to dynamically set the metadata for that page.
 * Pass the `title` and `description` props to customize the metadata. The URL is dynamically
 * generated based on the current route, and a default social media image is used.
 *
 * Example:
 * ```tsx
 * <Metadata
 *     title="Homepage"
 *     description="Explore General Genomics' cutting-edge data solutions."
 * />
 * ```
 *
 * Props:
 * - `title` (string): The title of the page. Appends " | General Genomics" automatically.
 * - `description` (string): A brief description of the page for SEO and social media.
 *
 * Features:
 * - Dynamically generates meta tags for:
 *   - Page title
 *   - Page description
 *   - Open Graph (OG) tags for social sharing
 *   - Twitter Card metadata
 * - Automatically appends " | General Genomics" to the title.
 * - Dynamically generates the canonical URL and OG URL based on the current route.
 * - Includes a default social media image (`/social.jpg`) for sharing if none is specified.
 * - Ensures SEO best practices with clean and consistent metadata.
 *
 * Notes:
 * - The `description` and `title` are required props to ensure proper metadata generation.
 * - Default social media image is stored in the public folder as `social.jpg`. Update the
 *   path if the default image changes.
 * - The component relies on `react-helmet` to inject metadata into the `<head>` of the document.
 * - Ensure the component is included on all key pages to maintain metadata consistency across the site.
 *
 * Example of Generated HTML:
 * ```html
 * <title>Homepage | General Genomics</title>
 * <meta name="description" content="Explore General Genomics' cutting-edge data solutions.">
 * <meta property="og:title" content="Homepage | General Genomics">
 * <meta property="og:description" content="Explore General Genomics' cutting-edge data solutions.">
 * <meta property="og:url" content="https://www.yoursite.com/current-page">
 * <meta property="og:image" content="https://www.yoursite.com/social.jpg">
 * <meta name="twitter:title" content="Homepage | General Genomics">
 * <meta name="twitter:description" content="Explore General Genomics' cutting-edge data solutions.">
 * <meta name="twitter:image" content="https://www.yoursite.com/social.jpg">
 * <link rel="canonical" href="https://www.yoursite.com/current-page">
 * ```
 */

import React from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';

interface MetaProps {
  title: string;
  description: string;
}

const Metadata: React.FC<MetaProps> = ({ title, description }) => {
  const location = useLocation();
  const currentUrl = `${window.location.origin}${location.pathname}`;
  const defaultImage = `${window.location.origin}/social.jpg`;

  const fullTitle = `${title} | General Genomics`; // Append " | General Genomics" to the title

  return (
    <Helmet>
      {/* Dynamic Title */}
      <title>{fullTitle}</title>
      <meta property="og:title" content={fullTitle} />
      <meta name="twitter:title" content={fullTitle} />

      {/* Dynamic Description */}
      <meta name="description" content={description} />
      <meta property="og:description" content={description} />
      <meta name="twitter:description" content={description} />

      {/* Dynamic URL */}
      <meta property="og:url" content={currentUrl} />
      <link rel="canonical" href={currentUrl} />

      {/* Default Social Media Image */}
      <meta property="og:image" content={defaultImage} />
      <meta name="twitter:image" content={defaultImage} />
      <meta name="image" content={defaultImage} />

      {/* General Meta Data */}
      <meta property="og:type" content="website"></meta>
    </Helmet>
  );
};

export default Metadata;
